import i18n from 'i18n';
import { toast } from 'helpers/toast';
import * as actions from 'reducers/lms/customGroupManagement/createCustomGroup/customGroup.action';
import * as types from 'reducers/lms/customGroupManagement/createCustomGroup/customGroup.actionTypes';
import { CustomGroupResponse } from 'reducers/lms/customGroupManagement/customGroupList/type.d';
import { takeLatest } from 'redux-saga/effects';
import { call, put } from 'typed-redux-saga';
import {
	fetchCustomGroupById,
	createCustomGroup as createCustomGroupService,
	updateCustomGroup as updateCustomGroupService,
	deleteCustomGroup as deleteCustomGroupService,
	CreateCustomGroupRequest,
	UpdateCustomGroupRequest,
	CustomGroupDeleteResponse,
} from 'services/lms/lmsCustomGroupManagement.service';
import { getCustomGroupList } from 'reducers/lms/customGroupManagement/customGroupList/customGroupList.action';

function* getCustomGroupById(action: ReturnType<typeof actions.fetchCustomGroupById.request>) {
	try {
		const { customGroupID } = action.payload;
		const response: CustomGroupResponse | null = yield* call(fetchCustomGroupById, customGroupID);

		if (response) {
			yield put(actions.fetchCustomGroupById.success({ data: response }));
		} else {
			yield put(actions.fetchCustomGroupById.failure({ error: 'Custom group not found' }));
			toast.error(i18n.t('message.lms.customGroupManagement.notFound'));
		}
	} catch (e) {
		yield put(actions.fetchCustomGroupById.failure({ error: 'Failed to fetch custom group' }));
		toast.error(i18n.t('message.lms.customGroupManagement.fetch.failed'));
	}
}

function* createCustomGroup(action: ReturnType<typeof actions.createCustomGroup.request>) {
	try {
		const { data } = action.payload;

		const payload: CreateCustomGroupRequest = {
			customGroupName: data.groupName,
			description: data.description || '',
			userIDs: data.userIDs || [],
			userGroupIDs: data.userGroupIDs || [],
		};

		const response: CustomGroupResponse | null = yield* call(createCustomGroupService, payload);

		if (response) {
			yield put(actions.createCustomGroup.success());
			toast.success(i18n.t('message.lms.customGroupManagement.create.success'));
		} else {
			yield put(actions.createCustomGroup.failure({ error: 'Failed to create custom group' }));
			toast.error(i18n.t('message.lms.customGroupManagement.create.failed'));
		}
	} catch (e) {
		yield put(actions.createCustomGroup.failure({ error: 'Failed to create custom group' }));
		toast.error(i18n.t('message.lms.customGroupManagement.create.failed'));
	}
}

function* updateCustomGroup(action: ReturnType<typeof actions.updateCustomGroup.request>) {
	try {
		const { groupID, data } = action.payload;

		const payload: UpdateCustomGroupRequest = {};

		// Only include fields that have values
		if (data.groupName) {
			payload.customGroupName = data.groupName;
		}
		if (data.description !== undefined && data.description !== null) {
			payload.description = data.description;
		}
		if (data.userIDs !== undefined) {
			payload.userIDs = data.userIDs;
		}
		if (data.userGroupIDs !== undefined) {
			payload.userGroupIDs = data.userGroupIDs;
		}

		const response: CustomGroupResponse | null = yield* call(updateCustomGroupService, groupID, payload);

		if (response) {
			yield put(actions.updateCustomGroup.success());
			toast.success(i18n.t('message.lms.customGroupManagement.update.success'));
		} else {
			yield put(
				actions.updateCustomGroup.failure({
					error: 'Failed to update custom group',
				}),
			);
			toast.error(i18n.t('message.lms.customGroupManagement.update.failed'));
		}
	} catch (e) {
		yield put(actions.updateCustomGroup.failure({ error: 'Failed to update custom group' }));
		toast.error(i18n.t('message.lms.customGroupManagement.update.failed'));
	}
}

function* deleteCustomGroup(action: ReturnType<typeof actions.deleteCustomGroup.request>) {
	try {
		const { customGroupID } = action.payload;

		const response: CustomGroupDeleteResponse | null = yield* call(deleteCustomGroupService, customGroupID);

		if (response) {
			yield put(actions.deleteCustomGroup.success({ customGroupID: response.customGroupID }));
			toast.success(i18n.t('message.lms.customGroupManagement.delete.success'));
			// Refresh the list after successful deletion
			yield put(getCustomGroupList.request({ limit: 15, page: 1 }));
		} else {
			yield put(actions.deleteCustomGroup.failure({ error: 'Failed to delete custom group' }));
			toast.error(i18n.t('message.lms.customGroupManagement.delete.failed'));
		}
	} catch (e) {
		yield put(actions.deleteCustomGroup.failure({ error: 'Failed to delete custom group' }));
		toast.error(i18n.t('message.lms.customGroupManagement.delete.failed'));
	}
}

export default function* customGroupSaga() {
	yield takeLatest(types.FETCH_CUSTOM_GROUP_BY_ID_REQUEST, getCustomGroupById);
	yield takeLatest(types.CREATE_CUSTOM_GROUP_REQUEST, createCustomGroup);
	yield takeLatest(types.UPDATE_CUSTOM_GROUP_REQUEST, updateCustomGroup);
	yield takeLatest(types.DELETE_CUSTOM_GROUP_REQUEST, deleteCustomGroup);
}
